import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-cm';
const USE_MOCK_DATA = false;

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

interface CMFormData {
    category: string;
    cmEventsInternalMonitoring: string;
    cmEventsInTriage: string;
    cmEventsInCoordination: string;
    cmNotableEscalations: string;
    cmIssuesBlockers: string;
    // Tiptap ProseMirror JSON mirrors of the rich-text fields above.
    cmEventsInternalMonitoringJson?: Record<string, any>;
    cmEventsInTriageJson?: Record<string, any>;
    cmEventsInCoordinationJson?: Record<string, any>;
    cmNotableEscalationsJson?: Record<string, any>;
    cmIssuesBlockersJson?: Record<string, any>;
}

const defaultForm: CMFormData = {
    category: 'CM',
    cmEventsInternalMonitoring: '',
    cmEventsInTriage: '',
    cmEventsInCoordination: '',
    cmNotableEscalations: '',
    cmIssuesBlockers: '',
};

const CMPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<CMFormData>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof CMFormData, value: string | number | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.cmpassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('CM');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'CM',
            cmEventsInternalMonitoring: b.cmEventsInternalMonitoring || '',
            cmEventsInTriage: b.cmEventsInTriage || '',
            cmEventsInCoordination: b.cmEventsInCoordination || '',
            cmNotableEscalations: b.cmNotableEscalations || '',
            cmIssuesBlockers: b.cmIssuesBlockers || '',
            cmEventsInternalMonitoringJson: b.cmEventsInternalMonitoringJson,
            cmEventsInTriageJson: b.cmEventsInTriageJson,
            cmEventsInCoordinationJson: b.cmEventsInCoordinationJson,
            cmNotableEscalationsJson: b.cmNotableEscalationsJson,
            cmIssuesBlockersJson: b.cmIssuesBlockersJson,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('CM Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const input: CreateShiftPassdownInput = { ...form };
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, input); }
            else { await createShiftPassdown(input); }
            resetForm();
            setSuccessMsg(isUpdate ? 'CM Passdown updated' : 'CM Passdown created');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // Render a CM rich-text field read-only. Prefers the Tiptap JSON when present
    // (rendered safely via RichTextContent); otherwise falls back to the legacy
    // plain-text string so older records still render as before.
    const renderRichField = (label: string, json?: Record<string, any>, value?: string) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                <h3>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    const hasContent = [
        form.cmEventsInternalMonitoring, form.cmEventsInTriage, form.cmEventsInCoordination,
        form.cmNotableEscalations, form.cmIssuesBlockers,
    ].every(val => !!richTextToPlain(val));
    const b0 = passdowns[0];

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label={<>Events in Internal Monitoring{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            autoFocus
                            value={form.cmEventsInternalMonitoringJson ?? formatPlainTextToHtml(form.cmEventsInternalMonitoring || '')}
                            placeholder="Events in internal monitoring..."
                            onChangeText={(text) => updateField('cmEventsInternalMonitoring', text)}
                            onChangeJson={(json) => updateField('cmEventsInternalMonitoringJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Events in Triage{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.cmEventsInTriageJson ?? formatPlainTextToHtml(form.cmEventsInTriage || '')}
                            placeholder="Events in triage..."
                            onChangeText={(text) => updateField('cmEventsInTriage', text)}
                            onChangeJson={(json) => updateField('cmEventsInTriageJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Events in Coordination{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.cmEventsInCoordinationJson ?? formatPlainTextToHtml(form.cmEventsInCoordination || '')}
                            placeholder="Events in coordination..."
                            onChangeText={(text) => updateField('cmEventsInCoordination', text)}
                            onChangeJson={(json) => updateField('cmEventsInCoordinationJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Notable Escalations{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.cmNotableEscalationsJson ?? formatPlainTextToHtml(form.cmNotableEscalations || '')}
                            placeholder="Notable escalations..."
                            onChangeText={(text) => updateField('cmNotableEscalations', text)}
                            onChangeJson={(json) => updateField('cmNotableEscalationsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Issues/Blockers{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.cmIssuesBlockersJson ?? formatPlainTextToHtml(form.cmIssuesBlockers || '')}
                            placeholder="Issues or blockers..."
                            onChangeText={(text) => updateField('cmIssuesBlockers', text)}
                            onChangeJson={(json) => updateField('cmIssuesBlockersJson', json)}
                        />
                    </FormField>

                    <div className="passdown-form__actions">
                        {requiredFieldHint()}
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update CM Passdown' : 'Submit CM Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No CM passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('cmShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.cmEventsInternalMonitoring) || richTextToPlain(b0.cmNotableEscalations)} ...</p>}

                            {activeExpanded && (
                                <div className="passdown-entry__fields-grid">
                                    {renderRichField('Events in Internal Monitoring', b0.cmEventsInternalMonitoringJson, b0.cmEventsInternalMonitoring)}
                                    {renderRichField('Events in Triage', b0.cmEventsInTriageJson, b0.cmEventsInTriage)}
                                    {renderRichField('Events in Coordination', b0.cmEventsInCoordinationJson, b0.cmEventsInCoordination)}
                                    {renderRichField('Notable Escalations', b0.cmNotableEscalationsJson, b0.cmNotableEscalations)}
                                    {renderRichField('Issues/Blockers', b0.cmIssuesBlockersJson, b0.cmIssuesBlockers)}
                                </div>
                            )}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 CM PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((b, idx) => {
                                    const isExpanded = expandedId === b.passdownId;
                                    return (
                                        <span key={b.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                <div className="passdown-history__content">
                                                    <div className="passdown-history__top">
                                                        <span className="passdown-history__date">
                                                            <FormattedDate date={b.createdAt} /> &middot; by {b.createdBy}
                                                            { b.updatedAt && b.updatedAt !== b.createdAt && (
                                                                <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b.updatedAt} /> &middot; by {b.updatedBy}</span>
                                                            )}
                                                        </span>
                                                        <div className="passdown-history__top-right">
                                                            {canModify('cmShiftPassdown', b.createdBy) && (
                                                                <ActionButtons
                                                                    onEdit={() => handleEdit(b)}
                                                                    onDelete={() => handleDeleteClick(b)}
                                                                    disabled={showForm}
                                                                />
                                                            )}
                                                            <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : b.passdownId)}>
                                                                <Icon
                                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                    variant="subtle"
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="passdown-history__details">
                                                            <div className="passdown-entry__fields-grid">
                                                                {renderRichField('Events in Internal Monitoring', b.cmEventsInternalMonitoringJson, b.cmEventsInternalMonitoring)}
                                                                {renderRichField('Events in Triage', b.cmEventsInTriageJson, b.cmEventsInTriage)}
                                                                {renderRichField('Events in Coordination', b.cmEventsInCoordinationJson, b.cmEventsInCoordination)}
                                                                {renderRichField('Notable Escalations', b.cmNotableEscalationsJson, b.cmNotableEscalations)}
                                                                {renderRichField('Issues/Blockers', b.cmIssuesBlockersJson, b.cmIssuesBlockers)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                <Modal
                    visible
                    header="Delete CM Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this CM passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default CMPassdown;
